import { useQuery } from '@tanstack/react-query';
import { useRouter, type Href } from 'expo-router';
import { useAtomValue } from 'jotai';
import { ChevronLeft } from 'lucide-react-native';
import { useMemo } from 'react';
import { Dimensions, ScrollView } from 'react-native';

import { AboutSection } from '@/components/profile/AboutSection';
import { AnonymousProfileView } from '@/components/profile/AnonymousProfileView';
import { DisclosuresSection } from '@/components/profile/DisclosuresSection';
import { EducationSection } from '@/components/profile/EducationSection';
import { ProfessionalSection } from '@/components/profile/ProfessionalSection';
import {
  WorkHistorySection,
  formatWorkHistoryItemContent,
} from '@/components/profile/WorkHistorySection';
import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
  ActionsheetDragIndicator,
  ActionsheetDragIndicatorWrapper,
  ActionsheetScrollView,
} from '@/components/ui/actionsheet';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { getCancerTypesV2QueryOptions } from '@/services/getCancerTypesV2';
import { getResponseTimeSettingsV2QueryOptions } from '@/services/getResponseTimeSettingsV2';
import { getUserPublicV2QueryOptions } from '@/services/getUserPublicV2';
import { getUserV2QueryOptions, UserV2 } from '@/services/getUserV2';
import { userIdAtom } from '@/services/globalState';
import { segmentClient } from '@/util/analytics';
import { shouldFetchUserScoped } from '@/util/authTokens';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';
import { mapToBioSections } from '@/util/mapBioSections';
import { isAskAnonymously } from '@/util/userFilters';
import { sortByYear } from '@/utils/profileHelpers';

type WorkHistoryItem = NonNullable<UserV2['workHistory']>[number];

interface ProfileSheetProps {
  isOpen: boolean;
  onClose: () => void;
  expertId: string;
  onReopenExpertSelect?: () => void;
}

/**
 * ProfileSheet Component
 *
 * Displays a user's profile in a mobile Actionsheet.
 * When closed, can optionally reopen the expert select sheet.
 */
export function ProfileSheet({
  isOpen,
  onClose,
  expertId,
  onReopenExpertSelect,
}: ProfileSheetProps) {
  const currentUserId = useAtomValue(userIdAtom);

  const isLoggedIn = !!currentUserId;

  const isOwnProfile = isLoggedIn && (expertId === currentUserId?.toString() || expertId === 'me');

  const canUsePrivateEndpoint = shouldFetchUserScoped(currentUserId);

  const { data: userDataV2, isLoading: userV2Loading } = useQuery({
    ...getUserV2QueryOptions(expertId),
    enabled: !!expertId && isOwnProfile && canUsePrivateEndpoint,
  });

  const { data: userDataPublic, isLoading: userPublicLoading } = useQuery({
    ...getUserPublicV2QueryOptions(expertId),
    enabled: !!expertId && (!isLoggedIn || !isOwnProfile),
  });

  const userData = isOwnProfile ? userDataV2 : userDataPublic;

  const userLoading = userV2Loading || userPublicLoading;

  const { data: cancerFocusData, isLoading: cancerFocusLoading } = useQuery(
    getCancerTypesV2QueryOptions()
  );

  const isLoading = userLoading || cancerFocusLoading;

  const userType = (userData?.userType || 'lmp') as 'lmp' | 'expert';

  const cancerFocusAreas = useMemo(
    () =>
      userData?.cancerFocusIds
        ?.map((cancerFocus) => {
          const currentFocus = cancerFocusData?.cancerTypeLookup.get(cancerFocus.id);

          return currentFocus?.label || '';
        })
        .filter(Boolean)
        .join(', ') || '',
    [userData, cancerFocusData]
  );

  const subspecialtyItems = useMemo(() => {
    if (!userData) {
      return [];
    }

    if ((userData as any).subspecialties) {
      const list = (userData as any).subspecialties as {
        label?: string;
      }[];

      return list.map((s) => s?.label || '').filter(Boolean);
    }

    return [];
  }, [userData]);

  const { data: responseTimeBuckets = [] } = useQuery(getResponseTimeSettingsV2QueryOptions());

  const responseTimeLabel = useMemo(() => {
    if (!userData?.responseTime) {
      return '';
    }

    const bucket = responseTimeBuckets.find(
      (b) => parseInt(userData.responseTime as string, 10) <= b.maxTime
    );

    return bucket?.label.replace(' time', '') || '';
  }, [userData?.responseTime, responseTimeBuckets]);

  const bioSections = useMemo(() => {
    const transformWorkHistoryItem = (workHistoryItem: WorkHistoryItem) => {
      const structuredContent = formatWorkHistoryItemContent(workHistoryItem);

      return {
        ...workHistoryItem,
        accolade: structuredContent.map((item) => item.text).join('\n'),
        accoladeParts: structuredContent,
      };
    };

    const workHistoryItems = (userData?.workHistory || []).map(transformWorkHistoryItem);

    const sortedWorkHistory = sortByYear(workHistoryItems);

    const disclosures = userType === 'expert' ? userData?.disclosures || [] : [];

    const allSections = mapToBioSections({
      workHistory: sortedWorkHistory,
      education: sortByYear(userData?.education || []),
      disclosures,
      about: userData?.blurb || '',
    });

    let finalSections = allSections.map((section) => {
      if (section.title === 'Work History') {
        return {
          ...section,
          data: section.data.map((item, index) => ({
            ...item,
            accoladeParts: sortedWorkHistory[index]?.accoladeParts,
          })),
        };
      }

      return section;
    });

    if (userType === 'expert' && !finalSections.some((s) => s.title === 'Disclosures')) {
      finalSections = [...finalSections, { title: 'Disclosures', data: [] }];
    }

    return finalSections;
  }, [userData, userType]);

  const { nameWithoutDoctor } = useMemo(
    () =>
      getProfileNameAndInitials({
        firstName: userData?.firstName || '',
        lastName: userData?.lastName || '',
        identifiesAsDoctor: isOwnProfile ? userData?.identifiesAsDoctor : undefined,
      }),
    [userData, isOwnProfile]
  );

  const router = useRouter();

  const handleClose = () => {
    onClose();

    if (onReopenExpertSelect) {
      onReopenExpertSelect();
    }
  };

  const sheetMaxHeight = Math.round(Dimensions.get('window').height * 0.92);

  const content =
    userData && isAskAnonymously(userData.lastName) ? (
      <AnonymousProfileView userId={userData.id || ''} expertId={expertId || ''} />
    ) : (
      <ScrollView
        className="w-full flex-1"
        contentContainerStyle={{ paddingBottom: 40 }}
        showsVerticalScrollIndicator={false}>
        <ProfessionalSection
          userData={userData}
          isOwnProfile={isOwnProfile}
          nameWithoutDoctor={nameWithoutDoctor}
          responseTimeLabel={responseTimeLabel}
          cancerFocusAreas={cancerFocusAreas}
          subspecialtyItems={subspecialtyItems}
          onEditSummary={() => {
            segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_STARTED, {
              editType: 'summary',
            });

            if (userData?.id) {
              router.push({
                pathname: '/profile/[id]/professional',
                params: { id: String(userData.id) },
              } as Href);
            }
          }}
          onSendMessage={() => {
            segmentClient.track(ANALYTICS_EVENTS.BIO_MESSAGE_SELECTED, {
              expert_id: expertId,
            });
          }}
          messageUrl={`/directory/inquiry/${userData?.id?.toString() || ''}`}
          showMessageButton={false}
        />
        {bioSections.map((section, index) => {
          if (section.title === 'Work History') {
            return (
              <View key={section.title} className="mt-10">
                <WorkHistorySection
                  section={section}
                  isFirstSection={index === 0}
                  isOwnProfile={isOwnProfile}
                  onEdit={() => {
                    segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_STARTED, {
                      editType: 'work-history',
                    });

                    if (userData?.id) {
                      router.push({
                        pathname: '/profile/[id]/work-history',
                        params: { id: String(userData.id) },
                      } as Href);
                    }
                  }}
                />
              </View>
            );
          }

          if (section.title === 'Education & Training') {
            return (
              <View key={section.title} className={index === 0 ? 'mt-8' : 'mt-10'}>
                <EducationSection
                  section={section}
                  isFirstSection={index === 0}
                  isOwnProfile={isOwnProfile}
                  onEdit={() => {
                    segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_STARTED, {
                      editType: 'education',
                    });

                    if (userData?.id) {
                      router.push({
                        pathname: '/profile/[id]/education',
                        params: { id: String(userData.id) },
                      } as Href);
                    }
                  }}
                />
              </View>
            );
          }

          if (section.title === 'About Me') {
            return (
              <AboutSection
                key={section.title}
                section={section}
                isFirstSection={index === 0}
                isOwnProfile={isOwnProfile}
                onEdit={() => {
                  segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_STARTED, {
                    editType: 'summary',
                  });

                  if (userData?.id) {
                    router.push({
                      pathname: '/profile/[id]/about',
                      params: { id: String(userData.id) },
                    } as Href);
                  }
                }}
              />
            );
          }

          if (section.title === 'Disclosures') {
            return (
              <DisclosuresSection
                key={section.title}
                section={section}
                isFirstSection={index === 0}
                isOwnProfile={isOwnProfile}
                onEdit={() => {
                  segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_STARTED, {
                    editType: 'disclosures',
                  });

                  if (userData?.id) {
                    router.push({
                      pathname: '/profile/[id]/disclosures',
                      params: { id: String(userData.id) },
                    } as Href);
                  }
                }}
              />
            );
          }

          return null;
        })}
      </ScrollView>
    );

  return (
    <Actionsheet isOpen={isOpen} onClose={handleClose}>
      <ActionsheetBackdrop />
      <ActionsheetContent
        className="w-full rounded-t-2xl p-0"
        style={{ maxHeight: sheetMaxHeight }}>
        <ActionsheetDragIndicatorWrapper>
          <ActionsheetDragIndicator />
        </ActionsheetDragIndicatorWrapper>

        <VStack className="z-20 w-full px-4 pb-6 pt-4">
          <Pressable
            onPress={handleClose}
            accessibilityRole="button"
            accessibilityLabel="Back"
            className="absolute left-0 top-0 shrink-0 p-2">
            <HStack className="items-center gap-1">
              <Icon as={ChevronLeft} className="h-7 w-7 text-typography-900" />
              <Text className="text-typography-900">Back</Text>
            </HStack>
          </Pressable>
        </VStack>

        <ActionsheetScrollView contentContainerClassName="px-4 pb-10">
          {isLoading ? <Spinner size="large" className="py-20" /> : content}
        </ActionsheetScrollView>
      </ActionsheetContent>
    </Actionsheet>
  );
}
